import React, { useEffect, useState } from 'react';
import { Check, Code2, ExternalLink, RefreshCw } from 'lucide-react';
import { getAuthToken } from '../../auth/context/AuthContext';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/jobs`;

/**
 * One embed code covering the company's whole job board.
 *
 * Deliberately not per job: a company with 200 roles cannot paste 200 snippets,
 * and would have to edit their careers page every time a role opens or closes.
 * This one is pasted once and never touched again.
 */
export default function EmbedJobBoardCard() {
  const [data, setData]     = useState(null);
  const [err, setErr]       = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    fetch(`${API}/embed-code`, { headers: { Authorization: `Bearer ${getAuthToken()}` } })
      .then(async (r) => {
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.detail || 'Could not load your embed code');
        }
        return r.json();
      })
      .then(setData)
      .catch((e) => setErr(e.message));
  }, []);

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // The clipboard API needs a secure context, so fall back to a hidden
      // textarea — otherwise Copy silently does nothing over plain http.
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    setCopied(which);
    setTimeout(() => setCopied(''), 2000);
  };

  if (err) {
    return (
      <div className="card">
        <div style={{ fontSize: 13, color: 'var(--danger, #c0392b)' }}>⚠ {err}</div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start' }}>
        <div>
          <div style={{
            fontSize: 15, fontWeight: 700, color: 'var(--text-primary)',
            marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Code2 size={17} strokeWidth={1.9} /> Universal job board embed
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: '54ch' }}>
            One code for every job. Paste it into your careers page once — openings
            appear and disappear on their own as you post and close them.
          </div>
        </div>
        <span style={{
          fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
          background: 'var(--bg-secondary)', color: 'var(--text-muted)', whiteSpace: 'nowrap',
        }}>
          {data.active_job_count} active {data.active_job_count === 1 ? 'job' : 'jobs'}
        </span>
      </div>

      <label className="form-label" style={{ marginTop: 18 }}>Embed code</label>
      <pre style={{
        margin: 0, padding: '13px 15px', borderRadius: 8, overflowX: 'auto',
        background: 'var(--bg-secondary)', border: '1px solid var(--border)',
        fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-primary)',
        fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
      }}>{data.iframe_embed_code}</pre>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        <button className="btn btn-primary" onClick={() => copy(data.iframe_embed_code, 'code')}>
          {copied === 'code'
            ? <><Check size={14} strokeWidth={2.2} /> Copied</>
            : 'Copy embed code'}
        </button>
        <button className="btn" onClick={() => copy(data.board_url, 'url')}>
          {copied === 'url' ? <><Check size={14} strokeWidth={2.2} /> Copied</> : 'Copy link only'}
        </button>
        <a
          className="btn"
          href={data.board_url}
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <ExternalLink size={14} /> Preview
        </a>
      </div>

      <div style={{
        marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)',
        fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.65,
      }}>
        <strong style={{ color: 'var(--text-primary)' }}>How it works.</strong>{' '}
        Candidates browse and apply without leaving your website — the application form
        and resume upload are inside the embed. Everyone who applies lands straight in
        your Candidates list and is scored like any other applicant.
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={13} />
          No need to update the code when your jobs change.
        </div>
      </div>
    </div>
  );
}
